/**
 * TanStack React Query key factory, fetch funkcije i hook-ovi za document API.
 * Koristi ActionPlan detail (documents tab) i komponente koje listaju dokumente po actionPlanId.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import DocumentService from '../../../shared/services/DocumentService';

/** Query key factory za document keš. */
export const documentKeys = {
  all: ['document'],
  forActionPlan: (actionPlanId) => [...documentKeys.all, 'actionPlan', actionPlanId],
};

const STALE_TIME_MS = 60 * 1000;

/**
 * Mapira dokument sa Font Awesome ikonom tipa fajla na osnovu ekstenzije.
 * @param {object} doc - Dokument iz API odgovora.
 * @returns {object} Dokument sa dodatim `fileType` poljem.
 */
function mapDocWithFileType(doc) {
  const extension = doc.name ? doc.name.split('.').pop().toLowerCase() : '';
  let fileType = 'fa-file';
  if (extension === 'pdf') fileType = 'fa-file-pdf';
  else if (['docx', 'doc'].includes(extension)) fileType = 'fa-file-word';
  else if (['xlsx', 'xls'].includes(extension)) fileType = 'fa-file-excel';
  else if (['pptx', 'ppt'].includes(extension)) fileType = 'fa-file-powerpoint';
  else if (['zip', 'rar'].includes(extension)) fileType = 'fa-file-archive';
  else if (['jpg', 'jpeg', 'png', 'gif'].includes(extension)) fileType = 'fa-file-image';
  return { ...doc, fileType };
}

/**
 * Fetch dokumenata za jedan akcioni plan.
 *
 * @param {string|number} actionPlanId - ID akcionog plana.
 * @param {AbortSignal} [signal] - Signal za otkazivanje zahteva.
 * @returns {Promise<object[]>} Niz dokumenata sa `fileType` ikonama.
 */
export async function fetchDocumentsForActionPlan(actionPlanId, signal) {
  if (!actionPlanId) return [];
  const docs = await DocumentService.search([actionPlanId], signal);
  return Array.isArray(docs) ? docs.map(mapDocWithFileType) : [];
}

/**
 * React Query hook za dokumente jednog akcionog plana. Keš po actionPlanId.
 *
 * @param {string|number} actionPlanId - ID akcionog plana.
 * @param {import('@tanstack/react-query').UseQueryOptions} [options] - Dodatne React Query opcije.
 * @returns {import('@tanstack/react-query').UseQueryResult<object[]>}
 */
export function useDocumentsForActionPlanQuery(actionPlanId, options = {}) {
  return useQuery({
    queryKey: documentKeys.forActionPlan(actionPlanId),
    queryFn: ({ signal }) => fetchDocumentsForActionPlan(actionPlanId, signal),
    enabled: !!actionPlanId,
    staleTime: STALE_TIME_MS,
    ...options,
  });
}

/**
 * Mutation za upload dokumenta. Invalidira listu dokumenata za akcioni plan na uspeh.
 *
 * @param {string|number} [actionPlanId] - ID akcionog plana za cache invalidaciju.
 * @returns {import('@tanstack/react-query').UseMutationResult}
 */
export function useDocumentUploadMutation(actionPlanId) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ file, name, relatedEntityIds }) =>
      DocumentService.upload(file, name, relatedEntityIds || [actionPlanId].filter(Boolean)),
    onSuccess: (_, variables) => {
      const apId = actionPlanId || variables?.relatedEntityIds?.[0];
      if (apId) {
        queryClient.invalidateQueries({ queryKey: documentKeys.forActionPlan(apId) });
      }
    },
  });
}

/**
 * Mutation za brisanje dokumenta. Invalidira listu dokumenata za akcioni plan na uspeh.
 *
 * @param {string|number} [actionPlanId] - ID akcionog plana za cache invalidaciju.
 * @returns {import('@tanstack/react-query').UseMutationResult}
 */
export function useDocumentDeleteMutation(actionPlanId) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ documentId }) => DocumentService.delete(documentId),
    onSuccess: () => {
      if (actionPlanId) {
        queryClient.invalidateQueries({ queryKey: documentKeys.forActionPlan(actionPlanId) });
      }
    },
  });
}
